'use client';

import React, { useRef, useState } from 'react';
import styled from '@benzinga/themetron';
import { Star } from './Star';

export interface RateProps {
  allowHalf?: boolean;
  className?: string;
  readOnly?: boolean;
  onClick?: (rating: number) => void;
  style?: React.CSSProperties;
  value?: number;
  size?: number;
}

export const Rate: React.FC<RateProps> = ({ allowHalf, className, onClick, readOnly = false, size, style, value }) => {
  const [activeStar, setActiveStar] = useState(value ?? -1);
  const [hoverActiveStar, setHoverActiveStar] = useState(-1);
  const [isHovered, setIsHovered] = useState(false);
  const ratingContainerRef = useRef<HTMLDivElement>(null);

  const totalStars = 5;
  const precision = allowHalf ? 0.5 : 1;

  const calculateRating = (e: React.MouseEvent<HTMLDivElement>): number => {
    if (ratingContainerRef.current) {
      const { left, width } = ratingContainerRef.current.getBoundingClientRect();
      const percent = (e.clientX - left) / width;
      const numberInStars = percent * totalStars;
      const nearestNumber = Math.round((numberInStars + precision / 2) / precision) * precision;
      const result = Number(nearestNumber.toFixed(precision.toString().split('.')[1]?.length || 0));
      return result;
    }
    return 0;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(false);
    const rating = calculateRating(e);
    setActiveStar(rating);
    if (onClick) {
      onClick(rating);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    setHoverActiveStar(calculateRating(e));
  };

  const handleMouseLeave = () => {
    setHoverActiveStar(-1);
    setIsHovered(false);
  };

  return (
    <RateContainer
      className={`rate-container ${className} ${readOnly ? 'readOnly' : ''}`}
      onClick={readOnly ? undefined : handleClick}
      onMouseLeave={handleMouseLeave}
      onMouseMove={readOnly ? undefined : handleMouseMove}
      ref={ratingContainerRef}
      style={style}
    >
      {Array.from({ length: 5 }, (_arr, index) => {
        const active = isHovered ? hoverActiveStar : activeStar;
        return <Star active={active} index={index} key={index} readOnly={readOnly} size={size} />;
      })}
    </RateContainer>
  );
};

const RateContainer = styled.div`
  &.rate-container {
    cursor: pointer;
    display: inline-flex;
    position: relative;
    text-align: left;
    height: 30px;
    align-items: center;
  }
  &.readOnly {
    cursor: auto;
    button,
    [role='button'] {
      cursor: auto;
    }
  }
`;

export default Rate;
